import { useState, type FormEvent } from 'react';
import { getBalances, getLocationItems, postLocation } from '../api/endpoints';
import { useApi } from '../api/hooks';
import { summarizeLocations } from '../api/adapt';
import type { BalanceRow, LocationSummary } from '../api/model';
import { Async, ErrorNotice } from '../components/Async';
import { CardHead } from '../components/Card';
import { Table, type Column } from '../components/Table';
import { Dash, Val } from '../components/Val';
import { formatDecimal } from '../lib/decimal';
import { formatUtc } from '../lib/time';
import { Link } from '../lib/router';

const balanceColumns: Column<BalanceRow>[] = [
  { key: 'loc', label: 'Helyszín', sortValue: (r) => r.location, render: (r) => r.location },
  { key: 'asset', label: 'Coin', sortValue: (r) => r.asset, render: (r) => r.asset },
  { key: 'origin', label: 'Eredet (ügylet)', help: 'origin', ariaLabel: 'eredet', sortValue: (r) => r.deal_id, render: (r) => (r.deal_id === null ? <span className="muted">saját tőke</span> : <Link to={`/deals/${r.deal_id}`}>#{r.deal_id} {r.deal_name}</Link>) },
  { key: 'qty', label: 'Mennyiség', numeric: true, help: 'quantity', ariaLabel: 'mennyiség', sortValue: (r) => r.quantity, render: (r) => <span className="mono">{formatDecimal(r.quantity)}</span> },
  { key: 'cost', label: 'Bekerülési érték, USD', numeric: true, help: 'costValue', ariaLabel: 'bekerülési érték', sortValue: (r) => r.cost_value_usd, render: (r) => <span className="mono"><Val value={r.cost_value_usd} reason={r.cost_reason} dp={2} /></span> },
  { key: 'value', label: 'Mai érték, USD', numeric: true, help: 'valueUsd', ariaLabel: 'mai érték', sortValue: (r) => r.value_usd, render: (r) => <span className="mono"><Val value={r.value_usd} reason={r.value_reason} dp={2} /></span> },
  {
    key: 'returnable', label: 'Visszahozandó', numeric: true, help: 'returnableCol', ariaLabel: 'visszahozandó',
    sortValue: (r) => r.returnable_outstanding,
    render: (r) => (r.returnable_outstanding === null ? <span className="muted">—</span> : <span className="mono warn">{formatDecimal(r.returnable_outstanding)} {r.returnable_asset}</span>),
  },
  {
    key: 'last', label: 'Utolsó mozgás (UTC)', help: 'lastMovement', ariaLabel: 'utolsó mozgás',
    sortValue: (r) => r.last_movement_at,
    render: (r) => (r.last_movement_at === null ? <Dash reason="no_data" /> : <span className="mono">{formatUtc(r.last_movement_at)}</span>),
  },
];

function LocationCard({ l }: { l: LocationSummary }) {
  return (
    <div className="card kpi compact" style={{ gap: 8 }}>
      <div className="card-head">
        <b>{l.name}</b>
        {l.api_backed ? <span className="pill api">API</span> : l.kind === 'asset' ? <span className="pill warn">Kézi</span> : <span className="pill">{l.kind}</span>}
      </div>
      <span className="mono" style={{ fontSize: 24, fontWeight: 600 }}>
        <Val value={l.value_usd} reason={l.value_reason} dp={0} />
      </span>
      <span className="small muted">{l.note}</span>
    </div>
  );
}

export function LocationsPage() {
  const locs = useApi(getLocationItems);
  const bal = useApi(getBalances);
  const [name, setName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<Error | null>(null);

  const add = async (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    setBusy(true);
    setErr(null);
    try {
      await postLocation({ name: name.trim(), venue: 'custom', with_loan_account: true });
      setName('');
      locs.reload();
      bal.reload();
    } catch (x) {
      setErr(x instanceof Error ? x : new Error(String(x)));
    } finally {
      setBusy(false);
    }
  };

  return (
    <main className="page">
      <div className="page-head">
        <div className="titles">
          <h1>Helyszínek</h1>
          <div className="muted">Hol van a hitelből származó pénz — helyszínenként és eredet (ügylet) szerint · mai érték USD</div>
        </div>
        <form className="row-wrap" style={{ alignItems: 'center' }} aria-label="Új helyszín" onSubmit={(e) => void add(e)}>
          <label htmlFor="newloc" className="sr-only">Új helyszín neve</label>
          <input id="newloc" className="input" placeholder="Új helyszín neve, pl. Ledger wallet" style={{ minWidth: 260 }} value={name} onChange={(e) => setName(e.target.value)} />
          <button type="submit" className="btn" disabled={busy || !name.trim()}>Helyszín felvétele</button>
        </form>
      </div>
      {err ? <ErrorNotice error={err} /> : null}

      <Async state={locs}>
        {(l) => (
          <section className="kpis compact" aria-label="Helyszínek összesítve">
            {bal.data
              ? summarizeLocations(l.filter((i) => i.kind === 'asset' && !i.archived), bal.data).map((i) => <LocationCard key={i.id} l={i} />)
              : l.filter((i) => i.kind === 'asset' && !i.archived).map((i) => <LocationCard key={i.id} l={{ ...i, value_usd: null, value_reason: 'no_data', note: 'egyenleg betöltése…' }} />)}
          </section>
        )}
      </Async>

      <section className="card tight" aria-label="Egyenleg">
        <CardHead title="Egyenleg helyszín × coin × eredet" hint="Csak a naplóban szereplő tételek; a Margin bot egyenlegei nincsenek benne (D1)" />
        <Async state={bal}>
          {(b) => (
            <Table
              columns={balanceColumns}
              rows={b.rows}
              rowKey={(r) => `${r.location_id}-${r.asset}-${r.deal_id ?? 'own'}`}
              defaultSort={{ key: 'value', dir: 'desc' }}
              label="Egyenleg helyszín, coin és eredet szerint"
              empty="Még nincs naplózott tétel."
              footer={
                b.rows.length ? (
                  <tr>
                    <td colSpan={4}>Összesen (helyszínek, USD)</td>
                    <td className="num mono"><Val value={b.total_cost_usd.value} reason={b.total_cost_usd.reason} dp={2} /></td>
                    <td className="num mono"><Val value={b.total_usd.value} reason={b.total_usd.reason} dp={2} /></td>
                    <td className="num mono warn">{b.returnable_totals.length ? b.returnable_totals.map((t) => `${formatDecimal(t.quantity)} ${t.asset}`).join(' + ') : <span className="muted">—</span>}</td>
                    <td />
                  </tr>
                ) : undefined
              }
            />
          )}
        </Async>
      </section>
    </main>
  );
}
